import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ApplicationService } from '../application/application.service';
import { ProjectModel } from '@project/model/project.model';
import { StringUtils } from '../util/string-utils';
import { LocaleResourceManager } from '../manager/locale-resource.manager';
import { SvgIconName } from '../svg-icon/svg-icon-name';
import { ProjectGroupUtils } from '@project/components/project/util/project-group-utils';
import { UserModel } from '@user/model/user.model';
import { SvgIconComponent } from '../svg-icon/svg-icon.component';
import { TooltipDirective } from '../tooltip/tooltip.directive';
import { TranslateModule } from '@ngx-translate/core';
import { SaveProjectService } from '@project/components/project/project-save/save-project.service';

@Component({
  selector: 'app-global-footer',
  templateUrl: './global-footer.component.html',
  imports: [
    SvgIconComponent,
    TooltipDirective,
    TranslateModule,
  ],
  styleUrls: ['./global-footer.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GlobalFooterComponent {
  private readonly projectModel: ProjectModel = inject(ProjectModel);
  private readonly localeResourceManager: LocaleResourceManager = inject(LocaleResourceManager);
  private readonly userModel: UserModel = inject(UserModel);
  private readonly saveProjectService: SaveProjectService = inject(SaveProjectService);

  private readonly currentProject = toSignal(this.projectModel.currentProject$);
  private readonly savedProject = toSignal(this.saveProjectService.savedProject$);
  private readonly localeChanged = toSignal(this.localeResourceManager.localeChanged$);

  public isProjectVersionVisible = computed<boolean>(() => {
    const currentProject = this.currentProject();
    return currentProject !== undefined
      && StringUtils.isNotEmpty(currentProject?.projectGroup?.groupName);
  });

  public projectGroupName = computed(() => {
    const currentProject = this.currentProject();
    if (currentProject && StringUtils.isNotEmpty(currentProject.projectGroup?.groupName)) {
      return currentProject.projectGroup?.groupName ?? '';
    } else {
      return this.localeResourceManager.getValueByKey('editProjectGroup.title.new_project');
    }
  });

  public projectGroupIcon = computed(() => {
    const currentProject = this.currentProject();
    if (currentProject?.projectGroup) {
      return ProjectGroupUtils.getProjectGroupIcon(currentProject.projectGroup, this.userModel.user);
    }
    return 'document' as SvgIconName;
  });

  public projectName = computed(() => {
    const currentProject = this.currentProject();
    if (currentProject && StringUtils.isNotEmpty(currentProject.name)) {
      return currentProject.name;
    } else {
      return this.localeResourceManager.getValueByKey('main.label.default_project_name');
    }
  });

  public projectVersion = computed(() => {
    this.localeChanged();
    const currentProject = this.currentProject();
    const savedProject = this.savedProject();
    const project = savedProject?.projectVO ?? currentProject;
    if (project) {
      const versionString = project?.currentVersionDisplayString ?? '?';
      const modificationDate = project?.modificationDate;
      if (modificationDate) {
        const date = new Date(modificationDate).toLocaleDateString(this.localeResourceManager.currentLanguage.langCode);
        const time = new Date(modificationDate).toLocaleTimeString(this.localeResourceManager.currentLanguage.langCode);
        return `${versionString} (${date} - ${time})`;
      }
      return versionString;
    }
    return '';
  });

  public articleNumberLong = computed(() => {
    const currentProject = this.currentProject();
    return currentProject?.articleNumberLong?.extendedArticleNumberLong?.trim() ?? '';
  });

  /** Will be "@sd_version@" during development. When building for production,
   * Maven patches the index.html file and replaces it with the actual version */
  public sdVersion = inject(ApplicationService).applicationVersion;

  public year: number = new Date().getFullYear();
}
